import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface LyricWord {
  timestamp: number;
  translation: string;   // English line to display
  break?: boolean;       // start a new display line before this entry
  section?: string;      // section label e.g. "Verse 1", "Chorus"
}

export interface Track {
  id: string;
  title: string;
  author: string;
  language: string;
  excerpt: string;
  dedicate_to: string;
  duration: string;
  storage_path: string;
  file_name: string;
  position: number;
  is_intro: boolean;
  is_outro: boolean;
  album_id: number;
  created_at: string;
  lyrics_status: string;
  lyrics: LyricWord[] | null;
  track_group_id: string | null;
  muted?: boolean;
}

export interface Album {
  id: number;
  tracks: Track[];
}

export function useTracks() {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [albumOrder, setAlbumOrder] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTracks = useCallback(async () => {
    setLoading(true);

    // Fetch tracks and album order in parallel
    const [tracksResult, albumsResult] = await Promise.all([
      supabase
        .from("tracks")
        .select("*")
        .order("album_id", { ascending: true })
        .order("position", { ascending: true }),
      supabase
        .from("albums")
        .select("id, display_position")
        .order("display_position", { ascending: true }),
    ]);

    if (tracksResult.error) {
      setError(tracksResult.error.message);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setTracks((tracksResult.data as unknown as Track[]) || []);
    }

    if (albumsResult.data && albumsResult.data.length > 0) {
      setAlbumOrder(albumsResult.data.map((a: { id: number }) => a.id));
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTracks();
  }, [fetchTracks]);

  // Group tracks by album_id, ordered by albumOrder (display_position)
  // Memoized so the array reference stays stable between renders.
  // Without this, usePlaybackEngine's queue effect reruns on every render,
  // continuously reshuffling the queue and preventing autostart.
  const albums: Album[] = useMemo(() => {
    const map = new Map<number, Track[]>();
    for (const t of tracks) {
      if (!map.has(t.album_id)) map.set(t.album_id, []);
      map.get(t.album_id)!.push(t);
    }

    const knownIds = Array.from(map.keys());
    let orderedIds: number[];

    if (albumOrder.length > 0) {
      const inOrder = albumOrder.filter((id) => map.has(id));
      const extras = knownIds.filter((id) => !albumOrder.includes(id)).sort((a, b) => a - b);
      orderedIds = [...inOrder, ...extras];
    } else {
      orderedIds = knownIds.sort((a, b) => a - b);
    }

    return orderedIds
      .filter((id) => map.has(id))
      .map((id) => ({ id, tracks: map.get(id)! }));
  }, [tracks, albumOrder]);

  /**
   * Public-facing albums with muted tracks filtered out.
   * Use this for listener-facing UI; admin uses `albums` to see/manage everything.
   * Albums that become empty after filtering are also removed.
   */
  const publicAlbums: Album[] = useMemo(() => {
    return albums
      .map((a) => ({ id: a.id, tracks: a.tracks.filter((t) => !t.muted) }))
      .filter((a) => a.tracks.length > 0);
  }, [albums]);

  /** Toggle muted state for a single track (admin-only). */
  const setTrackMuted = useCallback(async (trackId: string, muted: boolean) => {
    setTracks((prev) => prev.map((t) => (t.id === trackId ? { ...t, muted } : t)));
    const { error: updErr } = await supabase
      .from("tracks")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update({ muted } as any)
      .eq("id", trackId);
    if (updErr) {
      // Rollback
      setTracks((prev) => prev.map((t) => (t.id === trackId ? { ...t, muted: !muted } : t)));
      throw new Error(updErr.message);
    }
  }, []);

  const getPublicUrl = useCallback((storagePath: string) => {
    const { data } = supabase.storage.from("tracks").getPublicUrl(storagePath);
    return data.publicUrl;
  }, []);

  // Max album_id currently in use
  const maxAlbumId = tracks.length > 0 ? Math.max(...tracks.map((t) => t.album_id)) : 1;

  /** Total duration of all tracks in HH:MM:SS */
  const totalDuration = useMemo(() => {
    let totalSeconds = 0;
    for (const t of tracks) {
      if (!t.duration) continue;
      const parts = t.duration.split(":").map(Number);
      if (parts.length === 2) totalSeconds += parts[0] * 60 + parts[1];
      else if (parts.length === 3) totalSeconds += parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [tracks]);

  /** Upload a new track file + insert DB row */
  const uploadTrack = useCallback(
    async (
      file: File,
      meta: {
        title: string;
        author: string;
        language: string;
        excerpt: string;
        dedicate_to: string;
        duration?: string;
        album_id?: number;
      }
    ) => {
      const safeName = `${Date.now()}-${file.name.replace(/\s+/g, "-")}`;
      const storagePath = `uploads/${safeName}`;

      // Upload file to storage
      const { error: uploadErr } = await supabase.storage
        .from("tracks")
        .upload(storagePath, file, { contentType: file.type, upsert: false });

      if (uploadErr) throw new Error(uploadErr.message);

      const albumId = meta.album_id ?? 1;
      const isIntro = meta.title.trim().toLowerCase() === "intro";
      const isOutro = meta.title.trim().toLowerCase() === "outro";

      // Tracks in this album
      const albumTracks = tracks.filter((t) => t.album_id === albumId);

      // Determine position within album
      let targetPosition: number;
      if (isIntro) {
        targetPosition = 0;
      } else if (isOutro) {
        targetPosition = 999999;
      } else {
        const nonOutro = albumTracks.filter((t) => !t.is_outro);
        targetPosition =
          nonOutro.length > 0
            ? Math.max(...nonOutro.map((t) => t.position)) + 1
            : 1;
      }

      // If intro: push all existing tracks in this album down by 1
      if (isIntro && albumTracks.length > 0) {
        for (const t of albumTracks) {
          await supabase
            .from("tracks")
            .update({ position: t.position + 1 })
            .eq("id", t.id);
        }
      }

      // Ensure album row exists in albums table
      await supabase
        .from("albums")
        .upsert({ id: albumId, display_position: albumId }, { onConflict: "id", ignoreDuplicates: true });

      // Insert DB row
      const { error: insertErr } = await supabase.from("tracks").insert({
        title: meta.title,
        author: meta.author,
        language: meta.language,
        excerpt: meta.excerpt,
        dedicate_to: meta.dedicate_to,
        duration: meta.duration ?? "",
        storage_path: storagePath,
        file_name: file.name,
        position: targetPosition,
        is_intro: isIntro,
        is_outro: isOutro,
        album_id: albumId,
      });

      if (insertErr) throw new Error(insertErr.message);

      await fetchTracks();
    },
    [tracks, fetchTracks]
  );

  /** Persist reordered positions for a specific album's tracks */
  const saveOrder = useCallback(async (reordered: Track[]) => {
    if (reordered.length === 0) return;
    const albumId = reordered[0].album_id;
    setTracks((prev) => [
      ...prev.filter((t) => t.album_id !== albumId),
      ...reordered,
    ]);
    const updates = reordered.map((t, i) =>
      supabase.from("tracks").update({ position: i }).eq("id", t.id)
    );
    await Promise.all(updates);
  }, []);

  /** Persist the new display order of albums */
  const saveAlbumOrder = useCallback(async (orderedAlbumIds: number[]) => {
    // Optimistic update
    setAlbumOrder(orderedAlbumIds);
    const updates = orderedAlbumIds.map((id, i) =>
      supabase.from("albums").update({ display_position: i }).eq("id", id)
    );
    await Promise.all(updates);
  }, []);

  /**
   * Optimistically remove a track, persist to DB, re-index siblings.
   * Returns the previous tracks snapshot so callers can rollback on failure.
   */
  const deleteTrack = useCallback(
    async (trackId: string): Promise<Track[]> => {
      const snapshot = [...tracks];

      // Optimistic: remove from state
      setTracks((prev) => {
        const remaining = prev.filter((t) => t.id !== trackId);
        // Re-index positions within the affected album
        const deleted = prev.find((t) => t.id === trackId);
        if (!deleted) return remaining;
        const albumId = deleted.album_id;
        let counter = 0;
        return remaining.map((t) => {
          if (t.album_id !== albumId) return t;
          if (t.is_intro) return { ...t, position: 0 };
          if (t.is_outro) return { ...t, position: 999999 };
          return { ...t, position: counter++ };
        });
      });

      // Persist deletion
      const { error: delErr } = await supabase
        .from("tracks")
        .delete()
        .eq("id", trackId);

      if (delErr) {
        // Rollback
        setTracks(snapshot);
        throw new Error(delErr.message);
      }

      // Re-index siblings in DB
      const deleted = snapshot.find((t) => t.id === trackId);
      if (deleted) {
        const siblings = snapshot
          .filter((t) => t.album_id === deleted.album_id && t.id !== trackId)
          .sort((a, b) => a.position - b.position);
        let counter = 0;
        const updates = siblings.map((t) => {
          if (t.is_intro) return supabase.from("tracks").update({ position: 0 }).eq("id", t.id);
          if (t.is_outro) return supabase.from("tracks").update({ position: 999999 }).eq("id", t.id);
          return supabase.from("tracks").update({ position: counter++ }).eq("id", t.id);
        });
        await Promise.all(updates);
      }

      return snapshot;
    },
    [tracks]
  );

  return {
    tracks,
    albums,
    publicAlbums,
    albumOrder,
    loading,
    error,
    maxAlbumId,
    totalDuration,
    uploadTrack,
    saveOrder,
    saveAlbumOrder,
    deleteTrack,
    setTrackMuted,
    getPublicUrl,
    refetch: fetchTracks,
  };
}
